import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { PRODUCTS, PROFILES, PRODUCT_STUDIO_MAP } from "@/lib/constants";
import {
  ArrowRight, Flame, Droplets, Citrus, Beef,
  Baby, HeartHandshake, Activity, HeartPulse, ShieldCheck, Sparkles,
} from "lucide-react";

const badgeColorMap = {
  "Best Seller": "bg-spice-mango/10 text-spice-mango border-0",
  "Profile Friendly": "bg-earth-green/10 text-earth-green border-0",
  "Wellness": "bg-[#2E5C5A]/10 text-[#2E5C5A] border-0",
  "Family Pick": "bg-spice-mustard/10 text-spice-mustard border-0",
};

const profileIconMap = {
  "kid-friendly": Baby,
  "senior-focused": HeartHandshake,
  "diabetic-friendly": Activity,
  "heart-healthy": HeartPulse,
  "pregnancy-safe": ShieldCheck,
};

const TasteBars = ({ defaults }) => {
  const bars = [
    { label: "Spice", value: defaults.spice, color: "#9F3E18", icon: Flame },
    { label: "Salt", value: defaults.salt, color: "#2E5C5A", icon: Droplets },
    { label: "Sour", value: defaults.sourness || 0, color: "#65A30D", icon: Citrus },
    { label: "Rich", value: defaults.oil, color: "#D97706", icon: Beef },
  ];
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {bars.map((bar) => (
        <div key={bar.label} className="text-center">
          <div className="h-1 bg-cream-tertiary rounded-full overflow-hidden mb-1">
            <div className="h-full rounded-full transition-all" style={{ width: `${bar.value}%`, backgroundColor: bar.color }} />
          </div>
          <div className="flex items-center justify-center gap-0.5">
            <bar.icon size={9} style={{ color: bar.color }} />
            <span className="text-[9px] text-sw-text-muted">{bar.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ProductShowcase = () => {
  const navigate = useNavigate();
  return (
    <section id="products" data-testid="product-showcase-section" className="py-24 lg:py-36">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider">Our Collection</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
            Every pickle tells <span className="font-accent italic text-earth-green">your story</span>
          </h2>
          <p className="text-base text-sw-text-secondary mt-4">
            Each recipe starts with tradition and adapts to your taste, diet, and wellness needs.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          {PRODUCTS.map((product, index) => {
            const matchedProfiles = product.profiles
              .map((pid) => PROFILES.find((p) => p.id === pid))
              .filter(Boolean);

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`product-card-${product.id}`}
                className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm group cursor-pointer hover:shadow-lg transition-shadow duration-500"
                onClick={() => {
                  const mapping = PRODUCT_STUDIO_MAP[product.id];
                  if (mapping) navigate(`/studio?base=${mapping.base}&style=${mapping.style}`);
                  else navigate("/studio");
                }}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Image */}
                  <div className="sm:w-[45%] aspect-square sm:aspect-auto bg-cream-secondary p-4 relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-2xl group-hover:scale-105 transition-transform duration-700"
                    />
                    {product.badge && (
                      <Badge className={`absolute top-3 left-3 ${badgeColorMap[product.badge] || "bg-cream-secondary text-sw-text-secondary border-0"}`}>
                        {product.badge}
                      </Badge>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-5 lg:p-6 flex flex-col">
                    <p className="text-[10px] text-sw-text-muted uppercase tracking-widest mb-1">{product.category}</p>
                    <h3 className="text-lg font-heading font-700 text-sw-text mb-1.5">{product.name}</h3>
                    <p className="text-sm text-sw-text-secondary leading-relaxed mb-3">{product.description}</p>

                    {/* Taste Bars */}
                    <div className="mb-3">
                      <TasteBars defaults={product.defaults} />
                    </div>

                    {/* Key Ingredients */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.keyIngredients.map((ing) => (
                        <span key={ing} className="text-[10px] bg-cream-secondary text-sw-text-secondary px-2 py-0.5 rounded-full">
                          {ing}
                        </span>
                      ))}
                    </div>

                    {/* Compatible Profiles */}
                    {matchedProfiles.length > 0 && (
                      <div className="mb-3">
                        <p className="text-[10px] text-sw-text-muted uppercase tracking-wider mb-1.5">Works great with</p>
                        <div className="flex flex-wrap gap-1.5">
                          {matchedProfiles.map((profile) => {
                            const Icon = profileIconMap[profile.id];
                            return (
                              <span key={profile.id} className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full" style={{ backgroundColor: profile.color + "12", color: profile.color }}>
                                {Icon && <Icon size={10} />}
                                {profile.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Customize Hint */}
                    <div className="flex items-start gap-1.5 mb-4 mt-auto">
                      <Sparkles size={12} className="text-spice-mango shrink-0 mt-0.5" />
                      <p className="text-[11px] text-sw-text-muted italic leading-snug">{product.customizeHint}</p>
                    </div>

                    {/* Price + CTA */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div>
                        <span className="text-lg font-heading font-800 text-sw-text">&#8377;{product.price}</span>
                        <span className="text-[10px] text-sw-text-muted ml-1">onwards</span>
                      </div>
                      <span className="text-sm text-earth-green font-medium flex items-center gap-1 group-hover:gap-2 transition-all duration-300" data-testid={`product-customize-${product.id}`}>
                        Customize <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
